import { addCalendarDays, type CalendarDate } from "../shared/calendar-date";
import { requireFinitePositive, roundTo } from "../shared/validation";
import type { ProteinTargetResult } from "../goals/protein-policy";
import type { WeightTrend, WeightTrendSample } from "../weight/rolling-average";
import type { OnboardingWeightReference } from "../weight/onboarding-reference";
import type { CheckInQuestionnaire, CalorieRecommendation } from "./recommendation-policy";

export type CheckInTrendSource = "current_check_in" | "previous_check_in" | "onboarding_reference";

/** A value snapshot rather than a pointer to editable daily weight records. */
export interface CheckInTrendSnapshot {
  readonly policyVersion: string;
  readonly source: CheckInTrendSource;
  readonly date: CalendarDate;
  readonly averageKilograms: number;
  readonly samples: readonly WeightTrendSample[];
}

export interface CheckInProteinDecision {
  readonly policyVersion: string;
  readonly proposedDailyGrams: number;
  readonly acceptedDailyGrams: number;
  readonly wasOverridden: boolean;
  readonly gramsPerKilogram: number;
  readonly referenceWeightKilograms?: number;
  readonly referenceDate?: CalendarDate;
  readonly source: ProteinTargetResult["source"];
}

export interface CheckIn {
  readonly id: string;
  readonly weekPlanId: string;
  readonly dueDate: CalendarDate;
  readonly completedDate: CalendarDate;
  readonly completedAt: string;
  readonly questionnaire: CheckInQuestionnaire;
  readonly recommendation: CalorieRecommendation;
  /** User-reviewed value; can intentionally differ from recommendation. */
  readonly acceptedBaselineCalories: number;
  readonly baselineWasOverridden: boolean;
  readonly previousTrend?: CheckInTrendSnapshot;
  readonly currentTrend?: CheckInTrendSnapshot;
  readonly proteinDecision: CheckInProteinDecision;
  readonly nextWeekPlanId: string;
}

export function snapshotCurrentWeightTrend(trend: WeightTrend): CheckInTrendSnapshot {
  return {
    policyVersion: trend.policyVersion,
    source: "current_check_in",
    date: trend.date,
    averageKilograms: trend.averageKilograms,
    samples: trend.samples.map((sample) => ({ ...sample })),
  };
}

export function snapshotPreviousCheckInTrend(trend: CheckInTrendSnapshot): CheckInTrendSnapshot {
  return { ...trend, source: "previous_check_in", samples: trend.samples.map((sample) => ({ ...sample })) };
}

export function snapshotOnboardingTrend(reference: OnboardingWeightReference): CheckInTrendSnapshot {
  return {
    policyVersion: reference.trendPolicyVersion,
    source: "onboarding_reference",
    date: reference.date,
    averageKilograms: reference.weightKilograms,
    samples: reference.samples.map((sample) => ({ ...sample })),
  };
}

export function createCompletedCheckIn(input: CheckIn): CheckIn {
  if (!input.id.trim() || !input.weekPlanId.trim() || !input.nextWeekPlanId.trim()) throw new Error("Check-in identifiers are required");
  if (!input.completedAt.trim()) throw new Error("Check-in completion time is required");
  if (!Number.isInteger(input.acceptedBaselineCalories) || input.acceptedBaselineCalories <= 0) {
    throw new Error("Accepted baseline calories must be a positive whole number");
  }
  if (input.baselineWasOverridden !== (input.acceptedBaselineCalories !== input.recommendation.recommendedBaselineCalories)) {
    throw new Error("Baseline override flag does not match the accepted target");
  }
  if (input.previousTrend) validateTrendSnapshot(input.previousTrend, ["previous_check_in", "onboarding_reference"]);
  if (input.currentTrend) validateTrendSnapshot(input.currentTrend, ["current_check_in"]);
  if (input.currentTrend && input.currentTrend.date !== input.completedDate) {
    throw new Error("Current trend date must be the check-in completion date");
  }
  if (input.recommendation.previousTrendWeightKilograms !== input.previousTrend?.averageKilograms ||
    input.recommendation.currentTrendWeightKilograms !== input.currentTrend?.averageKilograms) {
    throw new Error("Recommendation trend weights must match the check-in snapshots");
  }
  validateProteinDecision(input.proteinDecision);
  if (input.proteinDecision.source === "current_weight_trend") {
    if (!input.currentTrend || input.proteinDecision.referenceDate !== input.currentTrend.date ||
      input.proteinDecision.referenceWeightKilograms !== input.currentTrend.averageKilograms) {
      throw new Error("Protein trend reference must match the current check-in trend snapshot");
    }
  }
  return {
    ...input,
    previousTrend: input.previousTrend ? cloneTrend(input.previousTrend) : undefined,
    currentTrend: input.currentTrend ? cloneTrend(input.currentTrend) : undefined,
    proteinDecision: { ...input.proteinDecision },
  };
}

function validateTrendSnapshot(snapshot: CheckInTrendSnapshot, sources: readonly CheckInTrendSource[]): void {
  if (!snapshot.policyVersion.trim()) throw new Error("Weight trend policy version is required");
  if (!sources.includes(snapshot.source)) throw new Error("Weight trend snapshot source is invalid");
  requireFinitePositive(snapshot.averageKilograms, "Trend average weight");
  if (snapshot.samples.length < 1 || snapshot.samples.length > 5) {
    throw new Error("A trend snapshot must contain between one and five samples");
  }
  const allowedDates = new Set(Array.from({ length: 5 }, (_, offset) => addCalendarDays(snapshot.date, -offset)));
  const seen = new Set<CalendarDate>();
  let total = 0;
  for (const sample of snapshot.samples) {
    requireFinitePositive(sample.kilograms, "Trend sample weight");
    if (!allowedDates.has(sample.date) || seen.has(sample.date)) throw new Error("Trend snapshot contains an invalid sample date");
    seen.add(sample.date);
    total += sample.kilograms;
  }
  if (Math.abs(roundTo(total / snapshot.samples.length, 3) - snapshot.averageKilograms) > 0.000_001) {
    throw new Error("Trend snapshot average does not match its samples");
  }
}

function validateProteinDecision(decision: CheckInProteinDecision): void {
  if (!decision.policyVersion.trim()) throw new Error("Protein target policy version is required");
  if (!Number.isInteger(decision.proposedDailyGrams) || decision.proposedDailyGrams <= 0) {
    throw new Error("Proposed daily protein must be a positive whole number");
  }
  if (!Number.isInteger(decision.acceptedDailyGrams) || decision.acceptedDailyGrams <= 0) {
    throw new Error("Accepted daily protein must be a positive whole number");
  }
  if (decision.wasOverridden !== (decision.acceptedDailyGrams !== decision.proposedDailyGrams)) {
    throw new Error("Protein override flag does not match the accepted target");
  }
  requireFinitePositive(decision.gramsPerKilogram, "Protein factor");
  if (decision.source === "current_weight_trend") {
    if (decision.referenceWeightKilograms === undefined || decision.referenceDate === undefined) {
      throw new Error("A trend-derived protein target requires its reference weight and date");
    }
    requireFinitePositive(decision.referenceWeightKilograms, "Protein reference weight");
  } else if (decision.referenceWeightKilograms !== undefined || decision.referenceDate !== undefined) {
    throw new Error("A retained protein target cannot claim a weight reference");
  }
}

function cloneTrend(trend: CheckInTrendSnapshot): CheckInTrendSnapshot {
  return { ...trend, samples: trend.samples.map((sample) => ({ ...sample })) };
}
